/**
 * Utility function to check if form data has changed by comparing current and new values.
 *
 * @param currentValues - Current form values
 * @param newValues - New form values to compare against
 * @returns true if data has changed, false otherwise
 */
export const hasFormDataChanged = (
  currentValues: any,
  newValues: any
): boolean => {
  return JSON.stringify(currentValues) !== JSON.stringify(newValues);
};

/**
 * Utility function to check if specific fields in form data have changed.
 *
 * @param currentValues - Current form values
 * @param newValues - New form values to compare against
 * @param fieldsToCheck - Array of field names to check for changes
 * @returns true if any of the specified fields have changed, false otherwise
 */
export const hasSpecificFieldsChanged = (
  currentValues: any,
  newValues: any,
  fieldsToCheck: string[]
): boolean => {
  return fieldsToCheck.some((field) => {
    const currentValue = currentValues[field];
    const newValue = newValues[field];

    if (Array.isArray(currentValue) && Array.isArray(newValue)) {
      return (
        currentValue.length !== newValue.length ||
        currentValue.some(
          (item, index) =>
            JSON.stringify(item) !== JSON.stringify(newValue[index])
        )
      );
    }

    return currentValue !== newValue;
  });
};

/**
 * Utility function to check if array items have changed by comparing their properties.
 *
 * @param currentArray - Current array of items
 * @param newArray - New array of items to compare against
 * @param keyFields - Array of field names to use for comparison
 * @returns true if any items have changed, false otherwise
 */
export const hasArrayItemsChanged = (
  currentArray: any[],
  newArray: any[],
  keyFields: string[]
): boolean => {
  if (currentArray.length !== newArray.length) {
    return true;
  }

  return newArray.some((item, index) => {
    const currentItem = currentArray[index];
    if (!currentItem) return true;

    return keyFields.some((field) => {
      const currentValue = currentItem[field];
      const newValue = item[field];

      if (Array.isArray(currentValue) && Array.isArray(newValue)) {
        return (
          currentValue.length !== newValue.length ||
          currentValue.some((val, i) => val !== newValue[i])
        );
      }

      return currentValue !== newValue;
    });
  });
};
